import { create } from 'zustand';
import type { CalendarIntegrationResponse } from 'api/generated/types/calendarIntegrationResponse';
import { CalendarIntegrationType, MeetingProvider } from 'api/generated/types';
import { CalendarProvider } from 'utils/calendarProvider';
import { isValidMeetingProvider } from 'utils/meetingProviderUtils';
import { registerSessionReset } from 'stores/sessionResetRegistry';

export type CalendarConnected = {
  calendarConnected: boolean;
  userCalendarConnected: boolean;
  provider: CalendarProvider | string;
  meetingProvidersAvailable: MeetingProvider[];
  calendarIntegrationType: CalendarIntegrationType | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: boolean;
};

export const INITIAL_CALENDAR_INTEGRATION: CalendarConnected = Object.freeze({
  calendarConnected: false,
  userCalendarConnected: false,
  provider: CalendarProvider.NONE,
  meetingProvidersAvailable: [MeetingProvider.NONE],
  calendarIntegrationType: null,
  isLoading: false,
  isLoaded: false,
  error: false,
});

function mapCalendarIntegrationResponse(
  data: CalendarIntegrationResponse
): Omit<CalendarConnected, 'isLoading' | 'isLoaded' | 'error'> {
  const {
    calendar,
    meeting_providers_available,
    calendar_integration_type,
    user_calendar_connected,
  } = data;

  const isIndividual =
    calendar_integration_type === CalendarIntegrationType.INDIVIDUAL;
  // Individual: org is "configured" once admin selects that mode (even before users connect).
  const connected = isIndividual ? !!calendar_integration_type : !!calendar;

  return {
    calendarConnected: connected,
    userCalendarConnected: isIndividual
      ? !!user_calendar_connected
      : !!calendar,
    provider: calendar || CalendarProvider.NONE,
    meetingProvidersAvailable: (meeting_providers_available || []).filter(
      isValidMeetingProvider
    ),
    calendarIntegrationType: calendar_integration_type ?? null,
  };
}

type CalendarIntegrationStoreState = CalendarConnected & {
  // Bumped by reset; a fetch records it before awaiting and drops its result if it
  // changed, so a response for the previous org cannot land after logout.
  generation: number;
  applyIntegration: (data: CalendarIntegrationResponse) => void;
  setLoading: (isLoading: boolean) => void;
  setError: () => void;
  invalidate: () => void;
  reset: () => void;
};

export const useCalendarIntegrationStore =
  create<CalendarIntegrationStoreState>()((set) => ({
    ...INITIAL_CALENDAR_INTEGRATION,
    generation: 0,
    applyIntegration: (data) =>
      set({
        ...mapCalendarIntegrationResponse(data),
        isLoading: false,
        isLoaded: true,
        error: false,
      }),
    setLoading: (isLoading) =>
      set(isLoading ? { isLoading: true, error: false } : { isLoading: false }),
    setError: () => set({ isLoading: false, isLoaded: true, error: true }),
    invalidate: () => set({ isLoaded: false }),
    reset: () =>
      set((state) => ({
        ...INITIAL_CALENDAR_INTEGRATION,
        generation: state.generation + 1,
      })),
  }));

registerSessionReset(() => useCalendarIntegrationStore.getState().reset());

export const selectCalendarConnected = (s: CalendarIntegrationStoreState) =>
  s.calendarConnected;
export const selectMeetingProvidersAvailable = (
  s: CalendarIntegrationStoreState
) => s.meetingProvidersAvailable;
export const selectCalendarProvider = (s: CalendarIntegrationStoreState) =>
  s.provider;
